import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import { GoogleMap, LoadScriptNext } from "@react-google-maps/api";
import { Marker } from "../../../../theme/maps";
import { formatUSPhone } from "../../../../utils";
import { colors } from "../../../ui/colors";
import { SASButtonStyles } from "../../../ui/styles/inputStyles";

export const StepWorkforceSummary = ({ data, onBack, onConfirm }) => {
  if (!data) return null;

  const isApplicant = data.status === "APPLICANT";
  const fullName = `${data.firstName || ""} ${data.lastName || ""}`.trim();
  const hasCoverage = data.coverageAreas && data.coverageAreas.length > 0;

  const emailList = data.emails && data.emails.length > 0 ? data.emails.map((email) => email.value).join(", ") : "—";

  const phoneList =
    data.phones && data.phones.length > 0 ? data.phones.map((p) => `${p.dialCode ? `${p.dialCode} ` : ""}${formatUSPhone(p.phone)}`).join(", ") : "—";

  const defaultCenter = hasCoverage
    ? {
        lat: parseFloat(data.coverageAreas[0].latitude),
        lng: parseFloat(data.coverageAreas[0].longitude),
      }
    : { lat: 39.7392, lng: -104.9903 };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        width: "100%",
      }}
    >
      {/* Título */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: colors.gray[800],
          mb: 2,
        }}
      >
        Review {isApplicant ? "Applicant" : "Employee"} Information
      </Typography>

      {/* Datos generales */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: `1px solid ${colors.gray[200]}`,
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: colors.orange.base }}>
          General Information
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography>
              <b>Full Name:</b> {fullName || "—"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <b>Employment Type:</b> {data.workforceRole || "—"}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography>
              <b>Email:</b> {emailList || "—"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <b>Phone:</b> {phoneList || "—"}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Cobertura */}
      {!isApplicant && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: `1px solid ${colors.gray[200]}`,
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          }}
        >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: colors.orange.base }}>
          Coverage Areas
        </Typography>

        {hasCoverage ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  maxHeight: 240,
                  overflowY: data.coverageAreas.length > 2 ? "auto" : "hidden",
                  pr: 1,
                }}
              >
                {data.coverageAreas.map((area, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${colors.orange[100]}`,
                      backgroundColor: colors.orange[50],
                      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {area.address}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.gray[600] }}>
                      {area.city}, {area.state}, {area.country}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Mini mapa */}
            <Grid item xs={12} md={6}>
              <LoadScriptNext googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                <GoogleMap
                  mapContainerStyle={{
                    width: "100%",
                    height: "240px",
                    maxHeight: "240px",
                    borderRadius: "10px",
                  }}
                  center={defaultCenter}
                  zoom={hasCoverage ? 11 : 4}
                  options={{ disableDefaultUI: true }}
                >
                  {data.coverageAreas.map((area, index) => (
                    <Marker
                      key={index}
                      position={{
                        lat: parseFloat(area.latitude),
                        lng: parseFloat(area.longitude),
                      }}
                    />
                  ))}
                </GoogleMap>
              </LoadScriptNext>
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body2" sx={{ color: colors.gray[500] }}>
            No coverage areas selected.
          </Typography>
        )}
      </Paper>
      )}

      {/* Botones */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 2 }}>
        <Button
          onClick={onBack}
          variant="outlined"
          sx={{
            borderRadius: "8px",
            px: 4,
            py: 1,
            fontWeight: 600,
            color: colors.gray[600],
            borderColor: colors.gray[300],
            textTransform: "uppercase",
            "&:hover": {
              borderColor: colors.gray[400],
              bgcolor: colors.gray[100],
            },
          }}
        >
          Back
        </Button>

        <Button
          onClick={onConfirm}
          sx={{
            ...SASButtonStyles,
            px: 5,
            backgroundColor: colors.orange.base,
          }}
        >
          Confirm & Save
        </Button>
      </Box>
    </Box>
  );
};
